import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Lock, LogIn, CheckCircle, AlertTriangle } from "lucide-react"; // Added CheckCircle
import { Link, useNavigate } from "react-router-dom"; // Import useNavigate for redirection

// 1. IMPORT FIREBASE SERVICES (Ensure this path is correct for your setup)
import { auth } from "../firebase1"; 
import { signInWithEmailAndPassword } from "firebase/auth";

// Framer Motion Variants
const formVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { type: "spring", stiffness: 100, damping: 15, delay: 0.1 } 
  },
};

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [firebaseError, setFirebaseError] = useState("");
  const [success, setSuccess] = useState(false); // State for success message
  
  const navigate = useNavigate(); // Initialize the navigate hook

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFirebaseError(""); 
    setSuccess(false); // Reset success state

    try {
      // 2. Perform Firebase Login
      await signInWithEmailAndPassword(auth, email, password);
      
      // Success: Show message briefly, then redirect
      setSuccess(true);
      
      setTimeout(() => {
        // 3. Use navigate to redirect to /dashboard
        navigate('/dashboard'); 
      }, 1500); // Redirect after 1.5 seconds

    } catch (error) {
      console.error("Firebase Login Error:", error);
      
      let message = "An unexpected error occurred. Please try again.";
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        message = "Invalid email or password. Please check your credentials.";
      } else if (error.code === 'auth/too-many-requests') {
        message = "Access temporarily blocked due to too many failed login attempts. Try again later.";
      }
      
      setFirebaseError(message);

    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-pink-100 to-yellow-100 p-4 sm:p-6">
      
      <motion.div
        className="w-full max-w-md bg-white p-8 sm:p-10 rounded-3xl shadow-2xl border-t-4 border-purple-500"
        variants={formVariants}
        initial="hidden"
        animate="visible"
      >
        {/* ... Header remains the same ... */}
        <header className="text-center mb-10">
          <motion.div
            initial={{ rotate: 0, scale: 0 }}
            animate={{ rotate: 360, scale: 1 }}
            transition={{ type: "spring", stiffness: 100, delay: 0.3 }}
            className="inline-block p-4 bg-purple-500 rounded-full shadow-lg mb-4"
          >
            <LogIn className="w-8 h-8 text-white" />
          </motion.div>
          <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight">
            Welcome Back!
          </h1>
          <p className="text-gray-500 mt-1">
            Sign in to continue your rhythm journey.
          </p>
        </header>

        {/* 4. SUCCESS MESSAGE (Conditional Display) */}
        {success && (
            <motion.div 
                initial={{ opacity: 0, y: -10 }} 
                animate={{ opacity: 1, y: 0 }} 
                className="p-3 mb-4 bg-green-100 border border-green-300 rounded-xl text-sm text-green-700 font-medium flex items-center gap-2"
            >
                <CheckCircle className="w-5 h-5 min-w-5" />
                Login successful! Redirecting to dashboard...
            </motion.div>
        )}
        
        {/* FIREBASE ERROR MESSAGE */}
        {firebaseError && (
            <motion.div 
                initial={{ opacity: 0, y: -10 }} 
                animate={{ opacity: 1, y: 0 }} 
                className="p-3 mb-4 bg-red-100 border border-red-300 rounded-xl text-sm text-red-700 font-medium flex items-center gap-2"
            >
                <AlertTriangle className="w-5 h-5 min-w-5" />
                {firebaseError}
            </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Field */}
          <motion.div initial={{ x: -10 }} animate={{ x: 0 }}>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFirebaseError(""); }}
                required
                placeholder="your.email@rhythm.com"
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all text-base"
                disabled={isLoading || success} // Disable during loading AND on success
              />
            </div>
          </motion.div>

          {/* Password Field */}
          <motion.div initial={{ x: 10 }} animate={{ x: 0 }}>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex justify-between">
                Password
                <a href="#" className="text-xs font-medium text-pink-500 hover:text-pink-600">Forgot?</a>
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setFirebaseError(""); }}
                required
                placeholder="••••••••"
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all text-base"
                disabled={isLoading || success} // Disable during loading AND on success
              />
            </div>
          </motion.div>

          {/* Login Button */}
          <motion.button
            type="submit"
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-xl font-bold text-lg text-white shadow-lg shadow-purple-300 transition-all disabled:bg-purple-400 disabled:shadow-none"
            whileHover={{ scale: (isLoading || success) ? 1 : 1.02, boxShadow: (isLoading || success) ? 'none' : "0 10px 15px -3px rgba(139, 92, 246, 0.4)" }}
            whileTap={{ scale: (isLoading || success) ? 1 : 0.98 }}
            disabled={isLoading || success} // Button is disabled when loading OR successful
          >
            {(isLoading || success) ? (
                <>
                    {/* Use CheckCircle on success, Spinner on loading */}
                    {success ? (
                        <CheckCircle className="h-5 w-5" />
                    ) : (
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    )}
                    {success ? 'Success!' : 'Signing In...'}
                </>
            ) : (
                <>
                    <LogIn className="w-5 h-5" />
                    Sign In
                </>
            )}
          </motion.button>
        </form>

        {/* Separator and Register Link */}
        <div className="mt-8 text-center">
            {/* ... Separator code ... */}
            {/* 5. Use <Link> for the Register navigation */}
            <motion.p 
                className="mt-6 text-sm text-gray-600"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
            >
                Don't have an account? 
                <Link to="/register" className="font-semibold text-pink-600 hover:text-pink-700 ml-1 transition-colors">
                    Register now!
                </Link>
            </motion.p>
        </div>

      </motion.div>
    </div>
  );
}